import React from 'react';
import { ConfigProvider } from 'antd';
import { useTheme } from '../../../theme/ThemeContext';
import AskAsperIcon from '../../../atoms/Icon/AskAsperIcon';
import TypewriterText from '../../../atoms/TypewriterText';
import { AgenticConversationMessage } from '../AskAsperAgentic.types';
import { parseLookupTokens, type LookupTextSegment } from '../utils/lookupTokenMarkers';

export interface ConversationMessageProps {
    message: AgenticConversationMessage;
    /** When true, animates string content with a typewriter effect */
    enableTypewriter?: boolean;
    /** Delay in ms per character for typewriter. Default: 25 */
    typewriterSpeed?: number;
    'data-test'?: string;
    'data-test-id'?: string;
}

/**
 * ConversationMessage - Displays a single conversation message (user or assistant)
 *
 * Supports:
 * - User messages with avatar/initials
 * - Assistant (AskAsper) messages with icon
 * - Markdown content rendering
 * - Custom React content
 */
const ConversationMessage: React.FC<ConversationMessageProps> = ({
    message,
    enableTypewriter = false,
    typewriterSpeed = 25,
    'data-test': dataTest,
    'data-test-id': dataTestId,
}) => {
    const theme = useTheme();
    const isUser = message.type === 'user';
    const isAssistant = message.type === 'assistant';

    // Get initials from user name
    const getInitials = (name: string): string => {
        // Remove "MC" prefix if present (e.g., "MC Marcus Chen" -> "Marcus Chen")
        const cleanName = name.replace(/^MC\s+/i, '').trim();
        const parts = cleanName.split(/\s+/);
        if (parts.length >= 2) {
            return (parts[0][0] + parts[1][0]).toUpperCase();
        }
        return cleanName.substring(0, 2).toUpperCase();
    };

    const getDisplayName = (name: string): string => {
        return name.replace(/^MC\s+/i, '').trim();
    };

    // Render a "plain" text segment (newlines + **bold** markers handled inline).
    const renderTextSegment = (text: string, keyPrefix: string): React.ReactNode => {
        return text.split('\n').map((line, index, array) => {
            const parts = line.split(/(\*\*.*?\*\*)/g);
            return (
                <React.Fragment key={`${keyPrefix}-line-${index}`}>
                    {parts.map((part, partIndex) => {
                        if (part.startsWith('**') && part.endsWith('**')) {
                            const boldText = part.slice(2, -2);
                            return (
                                <strong key={partIndex} className="font-semibold">
                                    {boldText}
                                </strong>
                            );
                        }
                        return <span key={partIndex}>{part}</span>;
                    })}
                    {index < array.length - 1 && <br />}
                </React.Fragment>
            );
        });
    };

    // Render the user-bubble string: split on /*entity*/ markers, highlight entity
    // segments as purple-tinted pills, and pass plain segments through the existing
    // newline + **bold** parser.
    const renderUserContentWithLookups = (content: string): React.ReactNode => {
        const segments = parseLookupTokens(content);
        return segments.map((seg: LookupTextSegment, idx: number) => {
            if (seg.type === 'entity') {
                return (
                    <span
                        key={`entity-${idx}`}
                        className="font-semibold text-[#6D28D9] bg-[#EFE6FA] rounded-[6px] px-1.5 py-0.5"
                        data-lookup-entity
                    >
                        {seg.text}
                    </span>
                );
            }
            return (
                <React.Fragment key={`text-${idx}`}>{renderTextSegment(seg.text, `seg-${idx}`)}</React.Fragment>
            );
        });
    };

    return (
        <ConfigProvider theme={theme}>
            <div
                className="flex gap-3"
                data-test={dataTest}
                data-test-id={dataTestId}
                data-conversation-message
                data-message-type={message.type}
            >
                {/* Avatar/Icon */}
                <div className="flex-shrink-0">
                    {isUser && message.userName ? (
                        <div className="w-8 h-8 rounded-full bg-[#F0E5F8] flex items-center justify-center">
                            <span className="font-['Open_Sans'] font-semibold text-sm leading-[16px] text-[#6D28D9]">
                                {getInitials(message.userName)}
                            </span>
                        </div>
                    ) : isAssistant ? (
                        <div className="w-6 h-6 rounded-full flex items-center justify-center overflow-hidden">
                            <div className="scale-[0.4] origin-center">
                                <AskAsperIcon size={48} />
                            </div>
                        </div>
                    ) : null}
                </div>

                {/* Message Content */}
                <div className="flex-1 min-w-0">
                    {/* User Name (for user messages) */}
                    {isUser && message.userName && (
                        <div className="mb-2">
                            <span className="font-['Open_Sans'] font-normal text-[16px] leading-[20px] text-black">
                                {getDisplayName(message.userName)}
                            </span>
                        </div>
                    )}

                    {/* Message Text/Content */}
                    {isUser ? (
                        <div className="bg-[#F8F6FC] rounded-[12px] px-4 py-3">
                            <div className="font-['Open_Sans'] font-normal text-[16px] leading-[24px] text-[#000000]">
                                {typeof message.content === 'string' ? (
                                    // User-typed text never needs the typewriter animation, and rendering
                                    // through this path preserves the highlighted `/*entity*\/` markers.
                                    <div className="whitespace-pre-wrap">
                                        {renderUserContentWithLookups(message.content)}
                                    </div>
                                ) : (
                                    message.content
                                )}
                            </div>
                        </div>
                    ) : (
                        <div className="font-['Open_Sans'] text-[16px] leading-[24px] text-[#52525C]">
                            {typeof message.content === 'string'
                                ? (() => {
                                      const content = message.content;
                                      const isGenerating =
                                          content === 'Generating...' || content.startsWith('Generating');
                                      const useTypewriter = enableTypewriter && !isGenerating;
                                      return useTypewriter ? (
                                          <TypewriterText
                                              text={content}
                                              speed={typewriterSpeed}
                                              parseBold
                                              contentKey={message.id != null ? `msg-${message.id}` : undefined}
                                          />
                                      ) : (
                                          <div className="whitespace-pre-wrap">
                                              {content.split('\n').map((line, index, array) => {
                                                  const parts = line.split(/(\*\*.*?\*\*)/g);
                                                  return (
                                                      <React.Fragment key={index}>
                                                          {parts.map((part, partIndex) => {
                                                              if (part.startsWith('**') && part.endsWith('**')) {
                                                                  const boldText = part.slice(2, -2);
                                                                  return (
                                                                      <strong key={partIndex} className="font-semibold">
                                                                          {boldText}
                                                                      </strong>
                                                                  );
                                                              }
                                                              return <span key={partIndex}>{part}</span>;
                                                          })}
                                                          {index < array.length - 1 && <br />}
                                                      </React.Fragment>
                                                  );
                                              })}
                                          </div>
                                      );
                                  })()
                                : message.content}
                        </div>
                    )}
                </div>
            </div>
        </ConfigProvider>
    );
};

export default ConversationMessage;
